import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * A device mockup drawn at its real size (in iOS points) and scaled down as
 * a whole by `--device-scale` (set it in `className`, per breakpoint), so
 * everything inside keeps the device's own measures. The box takes the
 * scaled size, so the slide's layout sees the phone as it looks.
 */
export function ScaledDevice({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const deviceRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null
  );

  useLayoutEffect(() => {
    const device = deviceRef.current;
    if (!device) return;
    // offsetWidth/offsetHeight ignore the transform: the device's own size.
    setSize({ width: device.offsetWidth, height: device.offsetHeight });
  }, []);

  return (
    <div
      className={cn('relative flex-none', className)}
      style={
        size
          ? {
              width: `calc(${size.width}px * var(--device-scale))`,
              height: `calc(${size.height}px * var(--device-scale))`,
            }
          : undefined
      }
    >
      <div
        ref={deviceRef}
        className="absolute left-0 top-0 origin-top-left"
        style={{ transform: 'scale(var(--device-scale))' }}
      >
        {children}
      </div>
    </div>
  );
}
